import AutoComplete from '@tarekraafat/autocomplete.js';
import Masonry from 'masonry-layout';
import {AddAnchor, ClearAnchor, GetAnchor} from '../../Common/functions/anchor';

const AutoCompleteSearch = element => {
    const autoCompleteInput = element.querySelector('[data-pavilion-autocomplete]');
    const src = JSON.parse(element.querySelector('[data-all-events-json]')?.innerText || '');
    const listViewItems = element.querySelectorAll('[data-list-view-item]');
    const autoCompleteJS = new AutoComplete({
        data: {
            src,
            cache: false,
            keys: ['title', 'summary']
        },
        events: {
            input: {
                focus() {
                    const inputValue = autoCompleteJS.input.value;
                    if (inputValue.length > 0) {
                        autoCompleteJS.start();
                    }
                },
                keyup() {
                    const inputValue = autoCompleteJS.input.value;

                    if (inputValue.length > 0) {
                        listViewItems.forEach(listViewItem => listViewItem.classList.add('hidden'));
                    } else {
                        listViewItems.forEach(listViewItem => listViewItem.classList.remove('hidden'));
                    }
                }
            },
        },
        placeHolder: autoCompleteInput.getAttribute('placeholder'),
        resultItem: {
            element: (item, data) => {
                item.innerHTML = `
                <div class="py-5 md:py-8 px-3 md:px-5 border-b border-green relative">
                    <dl class="grid sm:grid-cols-2 gap-y-5 gap-x-6">
                        <div class="font-bold">
                            <dt class="text-2xl md:text-3xl leading-snug">
                                <a href=" ${data.value.link}"
                                   class="before:absolute before:inset-0 before:z-10 underline hover:text-green transition-colors">
                                        ${data.key === 'title' ? data.match : data.value.title}
                                </a>
                            </dt>

                            <dd class="md:text-2xl mt-3 md:mt-4">
                                ${data.value.date}${data.value.rooms}
                            </dd>
                        </div>

                        <dd class="md:text-2xl max-w-[33rem]">
                            ${data.key === 'summary' ? data.match : data.value.summary}
                        </dd>
                    </dl>
                </div>`;
            },
            highlight: true
        },
        resultsList: {
            class: 'mt-9',
            maxResults: undefined,
            element: (list, data) => {
                const futureList = [];
                const pastList = [];
                const listItems = list.querySelectorAll('li');
                for (let i = 0; i < listItems.length; i++) {
                    const result = data.results[i];
                    if (result.value.comingUp) {
                        futureList.push(listItems[i]);
                    } else {
                        pastList.push(listItems[i]);
                    }
                }

                list.innerHTML = '';

                const headingClasses = 'text-h2 md:text-h2-lg leading-h2 md:leading-h2-lg uppercase';
                if (futureList.length) {
                    const comingUp = document.createElement('h3');
                    comingUp.classList = headingClasses;
                    comingUp.innerHTML = `Coming <span class="text-green">up...</span>`;

                    const li = document.createElement('li');
                    li.append(comingUp);
                    list.append(li);
                    futureList.forEach(item => list.append(item));
                }

                if (pastList.length) {
                    const pastEvents = document.createElement('h3');
                    pastEvents.classList = headingClasses;
                    if (futureList.length) {
                        pastEvents.classList.add('mt-14');
                    }

                    pastEvents.innerHTML = `Past <span class="text-green">events</span>`;

                    const li = document.createElement('li');
                    li.append(pastEvents);
                    list.append(li);
                    pastList.forEach(item => list.append(item));
                }

                const info = document.createElement('li');

                if (data.results.length > 0) {
                    info.innerHTML = `Displaying <strong>${data.results.length}</strong> out of <strong>${data.matches.length}</strong> results. `;
                } else {
                    info.innerHTML = `No results found for <strong>"${data.query}"</strong>. `;
                }

                const clearButton = document.createElement('button');
                clearButton.classList = 'underline hover:no-underline';
                clearButton.innerText = 'Clear search';
                clearButton.addEventListener('click', () => {
                    autoCompleteJS.close();
                    autoCompleteInput.value = '';
                    autoCompleteInput.blur();
                    listViewItems.forEach(listViewItem => listViewItem.classList.remove('hidden'));
                });

                info.append(clearButton);
                info.classList.add('text-2xl', 'mb-8');
                list.prepend(info);
            },
            noResults: true
        },
        selector: () => autoCompleteInput,
        threshold: 1,
    });
};


const Tabs = element => {
    const tabButtons = element.querySelectorAll('[data-tab-toggle-for]');
    const tabs = element.querySelectorAll('[data-tab-id]');

    const clearTabs = () => {
        tabs.forEach(tab => tab.classList.add('hidden'));
        tabButtons.forEach(button => button.parentElement.classList.add('border-b-transparent', 'font-bold'));
        tabButtons.forEach(button => button.parentElement.classList.remove('border-b-green', 'font-bold'));

        return true;
    };

    const makeActive = (button, tabID) => {
        const tab = element.querySelector(`[data-tab-id="${tabID}"]`);
        tab.classList.remove('hidden');
        button.parentElement.classList.remove('border-b-transparent', 'font-bold');
        button.parentElement.classList.add('border-b-green', 'font-bold');

        const masonryLayout = tab.querySelector('[data-masonry]');
        if (masonryLayout) {
            new Masonry(masonryLayout, {
                // options
                itemSelector: 'li',
                gutter: 24,
                percentPosition: true
            });
        }

        AddAnchor(tabID);
    };

    const handleAction = ({currentTarget}) => {
        if (currentTarget.classList.contains('border-b-green')) {
            return false;
        }

        clearTabs();
        makeActive(currentTarget, currentTarget.dataset.tabToggleFor);
    };

    tabButtons.forEach(button => button.addEventListener('click', handleAction));

    const anchor = GetAnchor();
    if (anchor) {
        const currentTarget = element.querySelector(`[data-tab-toggle-for="${anchor}"]`);

        if (currentTarget) {
            handleAction({currentTarget});
        }
    }
};

const ViewToggle = element => {
    const listViews = element.querySelectorAll('[data-list-view]');
    const dayViews = element.querySelectorAll('[data-day-view]');
    const toggle = element.querySelector('[data-pavilion-toggle]');
    const toggleBg = toggle.querySelector('div');
    const [listViewToggle, dayViewToggle] = toggle.querySelectorAll('button');

    const ShowListView = () => {
        listViews?.forEach((listView) => listView.classList.remove('hidden'));
        dayViews?.forEach((dayView) => dayView.classList.add('hidden'));
        toggleBg.classList.remove('left-1/2');
        toggleBg.classList.add('left-0');
        ClearAnchor();
    };

    const ShowDayView = () => {
        listViews?.forEach(listView => listView.classList.add('hidden'));
        dayViews?.forEach(dayView => dayView.classList.remove('hidden'));
        toggleBg.classList.add('left-1/2');
        toggleBg.classList.remove('left-0');

        const tabs = [...element.querySelectorAll('[data-tab-id]')]
            ?.filter(tab => !tab.classList.contains('hidden'));
        const anchor = tabs.length ? tabs[0]?.dataset.tabId : '';
        AddAnchor(anchor);

        if (tabs.length) {
            const masonryLayout = tabs[0]?.querySelector('[data-masonry]');
            if (masonryLayout) {
                new Masonry(masonryLayout, {
                    // options
                    itemSelector: 'li',
                    gutter: 24,
                    percentPosition: true
                });
            }
        }
    };

    listViewToggle?.addEventListener('click', ShowListView);
    dayViewToggle?.addEventListener('click', ShowDayView);
    const anchor = GetAnchor();
    if (anchor) {
        const currentTarget = element.querySelector(`[data-tab-toggle-for="${anchor}"]`);

        if (currentTarget) {
            ShowDayView();
        } else {
            ClearAnchor();
        }
    }
};

const PavilionList = () => {
    const init = element => {
        Tabs(element);
        ViewToggle(element);
        AutoCompleteSearch(element);
    };

    return document
        .querySelectorAll('[data-pavilion-list]')
        .forEach(init);
};

export default PavilionList;